import React, { useMemo } from 'react';
import { Group, Rect, Image, Text } from 'react-konva';
import type { ComputedBadge, BadgePosition } from '../../../core/assistant/models/ViewRules';
import { renderToStaticMarkup } from 'react-dom/server';
import useImage from 'use-image';
import * as LucideIcons from 'lucide-react';

const BADGE_SIZE = 20;
const BADGE_ICON_SIZE = 12;
const BADGE_OFFSET = 4;

const useDynamicLucideIcon = (iconName: string, size: number, color: string) => {
  const dataUrl = useMemo(() => {
    const pascalCase = iconName
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join('');

    const Icon = (LucideIcons as unknown as Record<string, LucideIcons.LucideIcon>)[pascalCase];
    if (!Icon) {
      return null;
    }

    const svg = renderToStaticMarkup(
      <Icon color={color} size={size} strokeWidth={2} />
    );
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }, [iconName, color, size]);

  const [image] = useImage(dataUrl || '');
  return dataUrl ? image : null;
};

const getBadgePosition = (
  position: BadgePosition,
  nodeWidth: number,
  nodeHeight: number
): { x: number; y: number } => {
  switch (position) {
    case 'top-right':
      return { x: nodeWidth - BADGE_SIZE / 2 - BADGE_OFFSET, y: -BADGE_SIZE / 2 + BADGE_OFFSET };
    case 'bottom-right':
      return { x: nodeWidth - BADGE_SIZE / 2 - BADGE_OFFSET, y: nodeHeight - BADGE_SIZE / 2 - BADGE_OFFSET };
    case 'bottom-left':
      return { x: -BADGE_SIZE / 2 + BADGE_OFFSET, y: nodeHeight - BADGE_SIZE / 2 - BADGE_OFFSET };
    case 'top-left':
      return { x: -BADGE_SIZE / 2 + BADGE_OFFSET, y: -BADGE_SIZE / 2 + BADGE_OFFSET };
    default:
      return { x: nodeWidth - BADGE_SIZE / 2 - BADGE_OFFSET, y: -BADGE_SIZE / 2 + BADGE_OFFSET };
  }
};

interface DisplayRuleBadgeProps {
  badge: ComputedBadge;
  nodeWidth: number;
  nodeHeight: number;
}

export const DisplayRuleBadge: React.FC<DisplayRuleBadgeProps> = ({
  badge,
  nodeWidth,
  nodeHeight,
}) => {
  const { x, y } = getBadgePosition(badge.position, nodeWidth, nodeHeight);
  const iconImage = useDynamicLucideIcon(badge.icon, BADGE_ICON_SIZE, '#FFFFFF');

  return (
    <Group x={x} y={y} listening={false}>
      <Rect
        width={BADGE_SIZE}
        height={BADGE_SIZE}
        fill={badge.color}
        cornerRadius={BADGE_SIZE / 2}
        shadowColor="rgba(0,0,0,0.2)"
        shadowBlur={2}
        shadowOffsetY={1}
      />
      {iconImage && (
        <Image
          image={iconImage}
          x={(BADGE_SIZE - BADGE_ICON_SIZE) / 2}
          y={(BADGE_SIZE - BADGE_ICON_SIZE) / 2}
          width={BADGE_ICON_SIZE}
          height={BADGE_ICON_SIZE}
        />
      )}
      {!iconImage && (
        <Text
          x={0}
          y={0}
          width={BADGE_SIZE}
          height={BADGE_SIZE}
          text="?"
          fontSize={12}
          fill="#FFFFFF"
          align="center"
          verticalAlign="middle"
        />
      )}
    </Group>
  );
};

interface DisplayRuleBadgesProps {
  badges: ComputedBadge[];
  nodeWidth: number;
  nodeHeight: number;
}

export const DisplayRuleBadges: React.FC<DisplayRuleBadgesProps> = ({
  badges,
  nodeWidth,
  nodeHeight,
}) => {
  if (badges.length === 0) return null;

  return (
    <>
      {badges.map((badge, index) => (
        <DisplayRuleBadge
          key={`${badge.icon}-${badge.position}-${index}`}
          badge={badge}
          nodeWidth={nodeWidth}
          nodeHeight={nodeHeight}
        />
      ))}
    </>
  );
};
